// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: turn a link the assistant pasted into a reply into something the
// preview window can open. The chips under a Home answer are built from the
// reply text, so this is what lets them preview instead of navigating away.

import type {AssistantSourceItem} from '@konvo/schema/src/domains/konvo/AssistantResponse';

const KONVO_MESSAGE = /^konvo:\/\/(?:open\/)?channels\/([^/]+)\/(\d{1,20})\/(\d{1,20})/i;
const KONVO_MAIL = /^konvo:\/\/(?:open\/)?mail\/messages\/([^/?#\s]+)/i;
const OUTLOOK_MESSAGE = /outlook\.(?:office|live|office365)\.com\/mail\/(?:[^/]+\/)*id\/([^/?#]+)/i;
const CLICKUP_TASK = /clickup\.com\/t\/(?:\d+\/)?([A-Za-z0-9_-]+)/i;

/** null when the link is just a link: those still open in the browser. */
export function sourceItemFromHref(href: string, label: string): AssistantSourceItem | null {
	const konvo = KONVO_MESSAGE.exec(href);
	if (konvo) {
		return {kind: 'konvo', title: label || 'Message', channel_id: konvo[2]!, message_id: konvo[3]!};
	}
	const konvoMail = KONVO_MAIL.exec(href);
	if (konvoMail) {
		const id = safeDecode(konvoMail[1]!);
		return id ? {kind: 'mail', title: label || 'Email', message_id: id} : null;
	}
	const mail = OUTLOOK_MESSAGE.exec(href);
	if (mail) {
		return {kind: 'mail', title: label || 'Email', message_id: decodeURIComponent(mail[1]!), url: href};
	}
	const task = CLICKUP_TASK.exec(href);
	if (task) {
		return {kind: 'clickup', title: label || 'Task', task_id: task[1]!, url: href};
	}
	if (/teams\.microsoft\.com/i.test(href)) {
		return {kind: 'teams', title: label || 'Teams message', url: href};
	}
	return null;
}

function safeDecode(value: string): string | null {
	try {
		return decodeURIComponent(value);
	} catch {
		return null;
	}
}
